import React from 'react';
import PropTypes from 'prop-types';
import { FieldArray, reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import Button from '../elements-buttons/Button';
import OpHoursRow from './OpHoursRow';
import { WeekData } from '../elements-date-time/data/week-data';
import onSubmitOpHoursForm from './forms/onSubmitOpHoursForm';
import { get, groupBy, reduce } from 'lodash';
import { toggleModal } from '../app-main/actions/modal';
import ModalManager from '../elements-modal/modal-manager-v2';
import BackModal from './modals/BackModal';
import ContinueModal from './modals/ContinueModal';
import InfoBanner from '../shared-components/banners/InfoBanner';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import SharedIntlProviderHOC from '../shared-components/hocs/sharedIntlProviderHOC';

const OpHoursForm = ({
  error,
  handleSubmit,
  isPosting,
  spaceId,
  isEdit,
  opHoursFormValue = {},
  enforceSocialEventOpHours = false,
  intl,
  ...props
}) => {
  const modalFn = (modalId, modalData) => {
    return props.dispatch(toggleModal({ modalId, modalData }));
  };
  const streetAddress = get(props, 'space.street', '');
  const spaceTitle = get(props, 'space.title', '');
  const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();
  const infoBannerBody = (<div>
    <FormattedMessage
      id="hosting.op_hours_form.op_hours_restriction_message"
      defaultMessage="Bookable hours end times are restricted to 12 AM for this space type."
    />&nbsp;
    <FormattedMessage
      id="hosting.op_hours_form.social_events_policy_cta"
      defaultMessage="Read our {link} for more information."
      values={{
        link: <a href="/legal/terms/social-event-policy" target="_blank">
          <FormattedMessage
            id="hosting.op_hours_form.social_events_policy_cta_link_text"
            defaultMessage="Social Events Policy"
          />
        </a>
      }}
    />
  </div>);

  return (
    <form className="OpHoursForm flex space-around" onSubmit={handleSubmit}>
      <div>
        <h2>
          <FormattedMessage
            id="hosting.op_hours_form.form_heading"
            defaultMessage="What are your bookable hours?"
          />
        </h2>
        <p>
          <FormattedMessage
            id="hosting.op_hours_form.form_message"
            defaultMessage="Bookable hours are the days and hours of the week that your space is open to host bookings (i.e. your general availability). Guests will not be able to book times outside of your bookable hours. {link}"
            values={{
              link: <a href={`//support.peerspace.com/hc/${zendeskLocale}/articles/360022408131`}>
                <FormattedMessage
                  id="hosting.op_hours_form.form_message_link_text"
                  defaultMessage="Learn More."
                />
              </a>
            }}
          />
        </p>
        <div className="heavy margin-xl-top margin-xl-bottom">
          {`${spaceTitle} - ${streetAddress}`}
        </div>
        <div>
          {
            enforceSocialEventOpHours &&
          <InfoBanner icon="info-black" body={infoBannerBody} classNames="flex margin-xl-bottom"/>
          }
        </div>

        <div className="OpHoursRowContainer">
          {
            WeekData.map((dow) => {
              const formValue = opHoursFormValue[dow.value];

              return (
                <div className="OpHoursRow" key={dow.id}>
                  <FieldArray
                    id={dow.id}
                    component={OpHoursRow}
                    name={`opHours.${dow.value}.dows`}
                    dowHash={dow}
                    formValue={formValue}
                    enforceSocialEventOpHours={enforceSocialEventOpHours}
                    {...props}
                  />
                </div>
              );
            })
          }
        </div>
        <div className="flex space-between margin-xl-top">
          <button
            type="button"
            onClick={() => modalFn('BackModal')}
            className="btn btn-default btn-outlined-light"
          >
            <FormattedMessage
              id="hosting.op_hours_form.back_button_text"
              defaultMessage="Back"
            />
          </button>
          <Button
            category="primary"
            className="submit-cta"
            disabled={false}
            attributes={{ 'data-testing-id': 'address' }}
            isLoading={isPosting}
          >
            { isEdit ?
              <FormattedMessage
                id="hosting.op_hours_form.done_button_text"
                defaultMessage="Done"
              /> :
              <FormattedMessage
                id="hosting.op_hours_form.next_button_text"
                defaultMessage="Next"
              />}
          </Button>
        </div>
        <div className="text-danger text-small margin-lg-top">{error}</div>
      </div>
      <ModalManager
        whichModal={get(props, 'modal.modalId')}
        toggleModal={modalFn}
      >
        <BackModal
          id="BackModal"
          isEdit={isEdit}
          spaceId={spaceId}
        />
        {
          isEdit &&
          <ContinueModal
            id="ContinueModal"
          />
        }
      </ModalManager>
    </form>
  );
};

OpHoursForm.propTypes = {
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  isPosting: PropTypes.bool,
  spaceId: PropTypes.string,
  isEdit: PropTypes.bool,
  opHoursFormValue: PropTypes.object,
  dispatch: PropTypes.func,
  intl: intlShape
};

const config = {
  form: 'OPHOURS_FORM',
  onChange: (values, dispatch, { clearSubmitErrors }) => {
    dispatch(clearSubmitErrors('OPHOURS_FORM'));
  },
  onSubmit: onSubmitOpHoursForm
};

const selector = formValueSelector('OPHOURS_FORM');

const mapStateToProps = (state) => {
  const operatingHours = get(state, 'calendar.operating_hours');
  const enforce_social_events_op_hours = get(state, 'calendar.enforce_social_events_op_hours');
  const accessToken = get(state, 'auth.accessToken');
  const space = get(state, 'space.data', {});
  const modal = get(state, 'modal');
  // group by day of the week
  const groupedOpHours = groupBy(operatingHours, 'dow');

  // fills in missing day of the week with empty array
  const opHours = reduce(WeekData, (result, dow) => {
    const dowValue = dow.value;
    const newResult = result;

    if (groupedOpHours[dowValue]) {
      newResult[dowValue] = { is_open: true, dows: groupedOpHours[dowValue] };
    } else {
      newResult[dowValue] = { is_open: false, dows: [{}] };
    }

    return result;
  }, {});

  return {
    accessToken,
    space,
    modal,
    opHoursFormValue: selector(state, 'opHours'),
    enforceSocialEventOpHours: enforce_social_events_op_hours,
    initialValues: {
      opHours
    }
  };
};

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(OpHoursForm)));
